import { PREVIEW_STATUS } from '@services/scoreSpecs';
import { formatUtcDate } from './format';

// The reading half of the preview dock: turning one normalized run row into things that are safe
// to say out loud.
//
// Every function here exists because the obvious render would assert something the contract does
// not promise. A preview result is now a COMPANY-UNIT, not a document, and three of its fields can
// be absent in ways that used to be impossible:
//   * a `failed_resolve` row may carry nothing but a trigger_key — no ticker, no company;
//   * `doc_ids` is a list OR null, and `[]` (ran on empty context) is a different fact from null
//     (the engine did not report it);
//   * `scores` is null for BOTH failure statuses, so the old "extraction missed" inference now
//     blames the prompt for what is often a retrieval problem.

const UNKNOWN_LABEL = 'Unidentified company-unit';

const STATUS_LABELS = {
  [PREVIEW_STATUS.COMPLETED]: 'Scored',
  [PREVIEW_STATUS.FAILED]: 'Extraction failed',
  [PREVIEW_STATUS.FAILED_RESOLVE]: 'Nothing to run on',
};

const STATUS_TONES = {
  [PREVIEW_STATUS.COMPLETED]: 'good',
  [PREVIEW_STATUS.FAILED]: 'bad',
  [PREVIEW_STATUS.FAILED_RESOLVE]: 'warn',
};

export const isKnownStatus = (status) => Object.prototype.hasOwnProperty.call(STATUS_LABELS, status);

export const statusLabel = (status) => STATUS_LABELS[status] || null;

export const statusTone = (status) => STATUS_TONES[status] || undefined;

const present = (value) => value !== null && value !== undefined && value !== '';

// Per FIELD, not per row. A row carrying a ticker and a null company_name must not render
// `AAPL — null`, and a failed_resolve row carrying only a trigger_key must not render
// `Company null`. The last resort names the row as unidentified rather than inventing an id.
export const companyLabel = (result) => {
  const { ticker, companyName, companyId, triggerKey } = result || {};
  const named = [present(ticker) ? String(ticker) : null, present(companyName) ? String(companyName) : null].filter(
    Boolean
  );
  if (named.length > 0) return named.join(' — ');
  if (present(companyId)) return `Company ${companyId}`;
  if (present(triggerKey)) return String(triggerKey);
  return UNKNOWN_LABEL;
};

// The identifiers that did NOT make it into the heading, so nothing is hidden. Returns null when
// there is nothing left to say rather than an empty line.
export const companyDetail = (result) => {
  const { companyId, triggerKey, ticker, companyName } = result || {};
  const headingUsedCompanyId = !present(ticker) && !present(companyName) && present(companyId);
  const parts = [];
  if (present(companyId) && !headingUsedCompanyId) parts.push(`company_id ${companyId}`);
  if (present(triggerKey)) parts.push(`trigger ${triggerKey}`);
  return parts.length > 0 ? parts.join(' · ') : null;
};

export const CONTEXT_KIND = {
  DOCUMENTS: 'documents',
  EMPTY: 'empty',
  NOT_REPORTED: 'not_reported',
};

// `[]` and null are different facts and get different sentences. Reading `.length` off the null
// case would throw during render, and the app mounts exactly one ErrorBoundary — at the root
// (app/App.js:73) — so a single malformed row would replace the whole editor, taking the unsaved
// buffer and the run just paid for with it.
export const contextSummary = (result) => {
  const docIds = (result || {}).docIds;
  if (!Array.isArray(docIds)) return { kind: CONTEXT_KIND.NOT_REPORTED, ids: [], count: 0 };
  if (docIds.length === 0) return { kind: CONTEXT_KIND.EMPTY, ids: [], count: 0 };
  return { kind: CONTEXT_KIND.DOCUMENTS, ids: docIds, count: docIds.length };
};

// A completed row with an empty map is not the same as a row that never got that far, and it is
// not the same as a row that scored. It is its own (rare, confusing) thing and says so.
export const hasNoScores = (result) => {
  const { scores } = result || {};
  return !!scores && typeof scores === 'object' && Object.keys(scores).length === 0;
};

// The run compiled the SAVED spec_json. If the model on the wire differs from the one in the
// buffer, that is proof — not a guess — that what ran is not what is on screen.
export const modelDrift = (runModel, bufferModel) =>
  !!runModel && !!bufferModel && String(runModel) !== String(bufferModel);

// The engine echoes the window it actually resolved. A difference is worth naming: the results
// describe the resolved window, not the one that was asked for.
// Compared at the granularity the header RENDERS. A raw string compare fires on a difference the
// operator cannot see — the engine echoing '2026-01-01T00:00:00Z' for an asked '2026-01-01' would
// print two identical dates under a banner saying they differ.
export const windowDrift = (asked, resolved) => {
  if (!asked || !resolved) return false;
  return (
    formatUtcDate(asked.from) !== formatUtcDate(resolved.from) || formatUtcDate(asked.to) !== formatUtcDate(resolved.to)
  );
};
